import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { pollPaymentStatus, claimPricingTableSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const session_id = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: null, plan: null, attempts: 0 });
  const stopped = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!session_id) {
      setState((s) => ({ ...s, status: "error" }));
      return;
    }
    const maxAttempts = 10;
    const tick = async (attempt) => {
      if (stopped.current) return;
      try {
        // Primary path: Stripe Pricing Table sessions (created by Stripe, not us).
        // The claim endpoint retrieves the session, infers the plan, and grants
        // access — no webhook required.
        try {
          const claim = await claimPricingTableSession(session_id);
          if (claim.status === "claimed" || claim.status === "already_claimed") {
            stopped.current = true;
            setState({ status: "paid", payment_status: "paid", plan: claim.plan, attempts: attempt });
            return;
          }
          if (claim.status === "not_paid" && attempt < maxAttempts) {
            setState({ status: "polling", payment_status: claim.payment_status, plan: null, attempts: attempt });
            setTimeout(() => tick(attempt + 1), 2000);
            return;
          }
        } catch (claimErr) {
          // Fall through to legacy poller — older sessions were created via our
          // own /payments/create-checkout and have a payment_transactions row.
        }

        const data = await pollPaymentStatus(session_id);
        if (data.payment_status === "paid") {
          stopped.current = true;
          setState({ status: "paid", payment_status: "paid", plan: data.plan, attempts: attempt });
          return;
        }
        if (data.status === "expired") {
          stopped.current = true;
          setState({ status: "expired", payment_status: data.payment_status, plan: data.plan, attempts: attempt });
          return;
        }
        if (attempt >= maxAttempts) {
          stopped.current = true;
          setState({ status: "timeout", payment_status: data.payment_status, plan: data.plan, attempts: attempt });
          return;
        }
        setState({ status: "polling", payment_status: data.payment_status, plan: data.plan, attempts: attempt });
        setTimeout(() => tick(attempt + 1), 2000);
      } catch (e) {
        if (attempt >= maxAttempts) {
          stopped.current = true;
          setState((s) => ({ ...s, status: "error" }));
          return;
        }
        setTimeout(() => tick(attempt + 1), 2000);
      }
    };
    tick(1);
    return () => { stopped.current = true; };
  }, [session_id]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <SiteHeader minimal />
      <main className="max-w-2xl mx-auto px-6 py-20 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#E27D60]" />
            <h1 className="font-display text-4xl mt-6">Confirming your payment…</h1>
            <p className="text-[#1E392A]/60 mt-2">Hang tight, this usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1E392A]" />
            <h1 className="font-display text-4xl mt-6">You're all set.</h1>
            <p className="text-sm text-[#1E392A]/65 mt-2">
              {state.plan === "agency"
                ? "Agency white-label is active. Add your logo on the Branding page."
                : state.plan === "monthly"
                  ? "Business Unlimited is active. Run as many analyses as you like."
                  : "Your one-time analysis credit has been added — your report stays available any time you sign in."}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Button onClick={() => navigate("/analyze")} data-testid="post-pay-start-btn" className="rounded-full bg-[#E27D60] hover:bg-[#cf6c50] text-white">
                Start your analysis
              </Button>
              <Link to="/dashboard" className="rounded-full px-5 inline-flex items-center text-sm border border-[#1E392A]/15 hover:bg-white" data-testid="post-pay-dashboard-link">
                Go to dashboard
              </Link>
            </div>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle className="w-10 h-10 mx-auto text-[#E27D60]" />
            <h1 className="font-display text-4xl mt-6">
              {state.status === "expired" ? "Session expired" : state.status === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-[#1E392A]/60 mt-2">
              {state.status === "timeout"
                ? "Your payment may take a moment longer. Check the dashboard in a minute."
                : "Try again — no charge if it didn't go through."}
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/billing" className="rounded-full bg-[#1E392A] hover:bg-[#152b20] text-white h-10 px-5 inline-flex items-center" data-testid="retry-billing-link">
                Back to billing
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
